import { cn } from '@/lib/utils';

export interface TimelineNode {
  key: string;
  label: string;
  /** The time, already formatted for the viewer's locale. */
  meta?: string;
  /** The node the order or the visit is sitting on right now. */
  current?: boolean;
}

/**
 * A rail of stamped events — the order's five steps, a visit's session.
 *
 * `horizontal` is the 4-node rail. It collapses to the vertical
 * form below `md` on purpose: a four-node 1152px rail has no phone form, and
 * the vertical one is the same information without a horizontal scrollbar.
 *
 * Rendered as an ordered list rather than a row of divs, because that is what
 * it is — a sequence — and it is the only way the order survives being read
 * aloud.
 */
export function Timeline({
  nodes,
  horizontal,
  className,
}: {
  nodes: readonly TimelineNode[];
  horizontal?: boolean;
  className?: string;
}) {
  if (!nodes.length) return null;

  return (
    <ol
      className={cn(
        horizontal
          ? 'grid gap-4 md:auto-cols-fr md:grid-flow-col'
          : 'space-y-0',
        className,
      )}
    >
      {nodes.map((node, index) => {
        const last = index === nodes.length - 1;
        return (
          <li
            key={node.key}
            className={cn(
              'relative',
              horizontal ? 'md:pe-4' : 'flex gap-3 pb-4 last:pb-0',
            )}
          >
            {horizontal ? (
              <>
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className={cn(
                      'h-2 w-2 shrink-0 rounded-full',
                      node.current ? 'bg-accent' : 'bg-accent/60',
                    )}
                  />
                  {!last && <span aria-hidden className="hidden h-px flex-1 bg-line md:block" />}
                </div>
                <p className="mt-2 text-sm font-medium">{node.label}</p>
                {node.meta && <p className="text-xs text-ink-muted">{node.meta}</p>}
              </>
            ) : (
              <>
                <div className="flex flex-col items-center">
                  <span
                    aria-hidden
                    className={cn(
                      'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                      node.current ? 'bg-accent' : 'bg-accent/60',
                    )}
                  />
                  {!last && <span aria-hidden className="mt-1 w-px flex-1 bg-line" />}
                </div>
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-3">
                  {node.meta && (
                    <span className="w-20 shrink-0 text-xs tabular-nums text-ink-muted">
                      {node.meta}
                    </span>
                  )}
                  <span className="text-sm">{node.label}</span>
                </div>
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
}
